import { Check, Phone, X } from "lucide-react";
import { useMemo } from "react";
import { useConfirm } from "../../components/ConfirmDialog";
import type { BookingStatus, ProviderBooking } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { formatEgp } from "../provider-orders/order-status";
import { formatLabel } from "../provider-workspace/provider-workspace-helpers";
import { getBookingStatusChipTone } from "./booking-utils";

/** Statuses that belong on the upcoming list; finished ones live under Past. */
const UPCOMING_STATUSES: BookingStatus[] = ["pending", "confirmed", "checked_in"];

function dayKey(iso: string, timeZone: string) {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
}

interface Props {
  bookings: ProviderBooking[];
  timezone: string;
  loading: boolean;
  readOnly: boolean;
  updatingStatusId: string | null;
  onOpen: (bookingId: string) => void;
  onStatus: (booking: ProviderBooking, status: BookingStatus) => Promise<unknown>;
}

/**
 * The next 30 days as a list grouped by day, with requests waiting for a yes or no
 * pinned on top. Confirm is one tap; declining asks first.
 */
export function UpcomingBookings({ bookings, timezone, loading, readOnly, updatingStatusId, onOpen, onStatus }: Props) {
  const { t, language } = useAppPreferences();
  const { confirm, dialog } = useConfirm();
  const locale = language === "ar" ? "ar-EG" : "en-US";
  const nowIso = new Date().toISOString();

  const upcoming = useMemo(
    () => bookings
      .filter((booking) => UPCOMING_STATUSES.includes(booking.status) && booking.scheduled_end_at > nowIso)
      .sort((a, b) => a.scheduled_start_at.localeCompare(b.scheduled_start_at)),
    [bookings, nowIso],
  );
  const requests = upcoming.filter((booking) => booking.status === "pending");
  const groups = useMemo(() => {
    const map = new Map<string, ProviderBooking[]>();
    for (const booking of upcoming) {
      const key = dayKey(booking.scheduled_start_at, timezone);
      map.set(key, [...(map.get(key) ?? []), booking]);
    }
    return Array.from(map.entries());
  }, [timezone, upcoming]);

  const today = dayKey(nowIso, timezone);
  const tomorrow = dayKey(new Date(Date.now() + 86400000).toISOString(), timezone);
  const dayTitle = (key: string, sample: string) => {
    if (key === today) return t("Today");
    if (key === tomorrow) return t("Tomorrow");
    return new Intl.DateTimeFormat(locale, { timeZone: timezone, weekday: "long", day: "numeric", month: "short" }).format(new Date(sample));
  };
  const time = (iso: string) => new Intl.DateTimeFormat(locale, { timeZone: timezone, hour: "numeric", minute: "2-digit" }).format(new Date(iso));
  const when = (booking: ProviderBooking) =>
    new Intl.DateTimeFormat(locale, { timeZone: timezone, weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }).format(new Date(booking.scheduled_start_at));

  const decline = async (booking: ProviderBooking) => {
    const ok = await confirm({
      title: t("Decline {name}'s booking?", { name: booking.customer_name }),
      description: t("{unit}, {when}. The player is told it was declined and the time becomes free again.", { unit: booking.unit_name, when: when(booking) }),
      confirmButtonLabel: t("Decline"),
      cancelButtonLabel: t("Keep request"),
    });
    if (ok) await onStatus(booking, "rejected");
  };

  const actions = (booking: ProviderBooking) => readOnly || booking.status !== "pending" ? null : (
    <div className="upcoming-actions">
      <button
        className="ops-button"
        data-size="sm"
        data-variant="primary"
        disabled={updatingStatusId === booking.id}
        onClick={(event) => { event.stopPropagation(); void onStatus(booking, "confirmed"); }}
        type="button"
      >
        <Check aria-hidden="true" size={14} /> {t("Confirm")}
      </button>
      <button
        aria-label={t("Decline {name}'s booking", { name: booking.customer_name })}
        className="ops-button"
        data-size="sm"
        data-variant="secondary"
        disabled={updatingStatusId === booking.id}
        onClick={(event) => { event.stopPropagation(); void decline(booking); }}
        type="button"
      >
        <X aria-hidden="true" size={14} /> {t("Decline")}
      </button>
    </div>
  );

  if (loading && bookings.length === 0) {
    return <p className="ops-copy py-6 text-sm">{t("Loading bookings…")}</p>;
  }

  return (
    <div className="grid gap-4">
      {dialog}
      {requests.length > 0 ? (
        <section className="upcoming-requests" aria-labelledby="upcoming-requests-title">
          <h3 className="home-section-title" id="upcoming-requests-title">
            {t(requests.length === 1 ? "{count} request to confirm" : "{count} requests to confirm", { count: requests.length })}
          </h3>
          <p className="ops-copy text-xs">{t("Players are waiting. Confirm to hold the time for them, or decline to free it.")}</p>
          <ul className="grid gap-2">
            {requests.map((booking) => (
              <li className="upcoming-request" key={booking.id}>
                <button className="upcoming-request-main" onClick={() => onOpen(booking.id)} type="button">
                  <span className="font-semibold">{booking.customer_name}</span>
                  <span className="ops-copy text-xs">{booking.unit_name} · {when(booking)}{booking.customer_phone ? ` · ${booking.customer_phone}` : ""}</span>
                </button>
                {actions(booking)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {groups.length === 0 ? (
        <div className="ops-empty py-8">
          <p>{t("No bookings in the next 30 days.")}</p>
        </div>
      ) : (
        groups.map(([key, dayBookings]) => (
          <section aria-label={dayTitle(key, dayBookings[0]!.scheduled_start_at)} className="grid gap-1.5" key={key}>
            <h3 className="upcoming-day">
              {dayTitle(key, dayBookings[0]!.scheduled_start_at)}
              <span className="ops-copy text-xs font-semibold">{t(dayBookings.length === 1 ? "{count} booking" : "{count} bookings", { count: dayBookings.length })}</span>
            </h3>
            <ul className="home-list">
              {dayBookings.map((booking) => (
                <li key={booking.id}>
                  <div className="upcoming-row">
                    <button className="home-list-row" onClick={() => onOpen(booking.id)} type="button">
                      <span className="home-list-time">{time(booking.scheduled_start_at)}–{time(booking.scheduled_end_at)}</span>
                      <span className="home-list-main">
                        <span className="truncate font-semibold">{booking.customer_name}</span>
                        <span className="ops-copy truncate text-xs">
                          {booking.unit_name}
                          {booking.customer_phone ? <> · <Phone aria-hidden="true" className="inline" size={11} /> {booking.customer_phone}</> : null}
                          {typeof booking.scheduled_total_amount === "number" ? ` · ${formatEgp(booking.scheduled_total_amount, language)}` : ""}
                        </span>
                      </span>
                      <span className="ops-chip shrink-0" data-tone={getBookingStatusChipTone(booking.status)}>{t(formatLabel(booking.status, t))}</span>
                    </button>
                    {actions(booking)}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
